import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Image } from '@/components/common/image';
import { HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { Slider } from '@/components/common/SliderCommon/Slider';
import { Section } from '@/components/layout/Section';
import { blocksToText } from '@/lib/sanityUtils';
import { blockUrlWithAudience } from '@/lib/utils';

import type {
  Audience,
  CompanyTestimonials as SanityCompanyTestimonials,
} from '@/sanity/sanity.types';
import type { BlockWithAudience, CTAProps, HotspotImage } from '@/types/shared';

interface TestimonialProps {
  _key?: string;
  _type?: 'testimonial';
  logo?: HotspotImage & {
    alternateText: string;
    isDecorativeImage?: boolean;
  };
  quote: string;
  author?: string;
  company?: string;
  ctaButton?: Pick<CTAProps, 'buttonLink' | 'trackingTag'> & {
    _type?: 'ctaButton';
    buttonLabel?: string;
    audienceRef?: Audience;
    noAudienceToggle?: boolean;
  };
}
export interface CompanyTestimonialsProps
  extends Omit<SanityCompanyTestimonials, 'testimonials'>,
    BlockWithAudience {
  testimonials: Array<TestimonialProps>;
}

/**
Mimimum of three - use this component with at least three items.

Don't put too much text in here. It should be light and quick to read.
*/

const CompanyTestimonials = ({
  heading,
  testimonials,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
  pageAudience,
  isAudienceSwitcherEnabled,
}: CompanyTestimonialsProps) => {
  const headingPlainText = blocksToText(heading);
  const sliderSettings = {
    dots: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    rows: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 640,
        settings: {
          slidesPerRow: 1,
          dots: false,
        },
      },
    ],
  };
  const backgroundColourToken =
    backgroundColour?.token || '--background-subtle';

  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColourToken}
      className="company-testimonials"
    >
      <HeadingLevel>
        <TestimonialsContainer>
          <StyledCaptionHeadingDescription
            heading={heading}
            desktopAlignment="center"
            mobileAlignment="center"
          />
          <Slider
            settings={sliderSettings}
            heading={headingPlainText}
            slideItems={testimonials}
            slideItemTitleKey="company"
          >
            {testimonials.map((testimonial) => (
              <div key={testimonial._key}>
                <Testimonial>
                  <LogoWrapper>
                    <Image {...testimonial.logo} sizes="440px" />
                  </LogoWrapper>
                  <Content>
                    <HeadingLevel>
                      <Quote size="subheading-sm">{testimonial.quote}</Quote>
                      {testimonial.author && testimonial.company && (
                        <HeadingLevel>
                          <Author size="subheading-sm">
                            {testimonial.author}, <b>{testimonial.company}</b>
                          </Author>
                        </HeadingLevel>
                      )}
                    </HeadingLevel>
                    {testimonial.ctaButton?.buttonLink && (
                      <HeadingLevel>
                        <Button
                          href={blockUrlWithAudience(
                            testimonial.ctaButton.buttonLink,
                            testimonial?.ctaButton?.audienceRef,
                            pageAudience,
                            testimonial.ctaButton.noAudienceToggle,
                            isAudienceSwitcherEnabled,
                          )}
                          className={
                            testimonial.ctaButton.trackingTag?.blockName &&
                            `tracking-${testimonial.ctaButton.trackingTag.blockName}__${testimonial.ctaButton.trackingTag.blockAction}`
                          }
                          variant="flat-light"
                        >
                          {testimonial.ctaButton.buttonLabel}
                        </Button>
                      </HeadingLevel>
                    )}
                  </Content>
                </Testimonial>
              </div>
            ))}
          </Slider>
        </TestimonialsContainer>
      </HeadingLevel>
    </Section>
  );
};

const TestimonialsContainer = styled.section`
  .slick-track {
    display: flex !important;
    align-items: center !important;
  }
`;

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  margin-bottom: var(--spacing-500);
`;

const Testimonial = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--spacing-800) 0;

  button {
    margin: 0 auto;
  }

  ${screen.md} {
    flex-direction: row;
    text-align: left;

    button {
      margin: 0;
    }
  }

  .slick-track {
    display: flex;
    align-items: center;
  }
`;

const contentGap = '64px';

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-1600);
  position: relative;
  overflow: hidden;
  width: 100%;

  > img {
    max-width: 260px;
    width: 100%;
  }

  ${screen.md} {
    flex: 0 0 50%;
    margin-bottom: 0;
    padding-right: ${contentGap};
    width: auto;

    > img {
      max-width: 440px;
    }
  }
`;

const Content = styled.div`
  max-width: 780px;

  ${screen.md} {
    border-left: 1px solid rgba(var(--colour-black-rgb), 0.1);
    flex: 0 0 50%;
    padding: 0 ${contentGap};
  }

  p:last-of-type {
    margin-bottom: 0px;
  }

  a {
    margin-top: 32px;
  }
`;

const Quote = styled(Paragraph)`
  font-weight: var(--font-weight-regular) !important;
  margin-bottom: 32px;
`;

const Author = styled(Paragraph)`
  font-weight: var(--font-weight-regular) !important;

  b {
    display: block;
  }

  ${screen.sm} {
    b {
      display: inline;
    }
  }
`;

export { CompanyTestimonials };
